import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTheme } from 'next-themes';
import fernauLogo from '@/assets/fernau-logo.png';
import { LocalizedLink } from '@/components/LocalizedLink';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useLang, useLocalizedPath, useT, stripLangPrefix } from '@/lib/i18n';

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const lang = useLang();
  const t = useT();
  const localize = useLocalizedPath();
  const currentBasePath = stripLangPrefix(location.pathname);

  const navItems = [
    { label: t('Startseite', 'Home'), href: '/' },
    { label: t('Über uns', 'About'), href: '/ueber-uns' },
    { label: t('Zerspanung', 'Machining'), href: '/leistungen' },
    { label: t('Consulting', 'Consulting'), href: '/consulting' },
    { label: t('Maschinenpark', 'Machine Park'), href: '/maschinenpark' },
    { label: t('Karriere', 'Careers'), href: '/karriere' },
    { label: t('Nachhaltigkeit', 'Sustainability'), href: '/nachhaltigkeit' },
    { label: t('Kontakt', 'Contact'), href: '/kontakt' },
  ];
  const ctaLabel = t('Anfrage starten', 'Start enquiry');
  const isoLabel = t('ISO 9001 Zertifiziert', 'ISO 9001 Certified');
  const hoursLabel = t('Mo-Do 7:30-16:00 | Fr 7:30-12:30', 'Mon-Thu 7:30-16:00 | Fri 7:30-12:30');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Top Bar */}
      <div className="hidden lg:block bg-secondary/80 border-b border-border/30">
        <div className="container mx-auto px-6 py-2 flex justify-between items-center text-sm text-muted-foreground">
          <div className="flex items-center gap-6">
            <a href="tel:+4961501849730" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Phone className="w-3.5 h-3.5" />
              <span className="font-mono">06150 184973-0</span>
            </a>
            <a href="mailto:info@fernau-gmbh.de" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Mail className="w-3.5 h-3.5" />
              <span>info@fernau-gmbh.de</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-mono uppercase tracking-wider">{isoLabel}</span>
            <span className="w-px h-4 bg-border/50" />
            <span className="text-xs">{hoursLabel}</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={cn(
          "sticky top-0 z-50 transition-all duration-300",
          isScrolled
            ? "bg-background/95 backdrop-blur-xl shadow-steel border-b border-border/30"
            : "bg-background/95 backdrop-blur-xl lg:bg-transparent lg:backdrop-blur-none"
        )}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <LocalizedLink to="/" className="flex items-center group">
              <img
                src={fernauLogo}
                alt="Fernau Präzisionstechnik GmbH"
                className="h-10 sm:h-12 w-auto"
              />
            </LocalizedLink>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => (
                <LocalizedLink
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "nav-link",
                    currentBasePath === item.href && "active"
                  )}
                >
                  {item.label}
                </LocalizedLink>
              ))}
            </nav>

            {/* CTA + Theme Toggle */}
            <div className="hidden lg:flex items-center gap-3">
              <LanguageSwitcher />
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                aria-label={t('Theme wechseln', 'Toggle theme')}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-200"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              {currentBasePath === '/consulting' ? (
                <Button variant="hero" size="lg" asChild>
                  <a href="#consulting-form">{ctaLabel}</a>
                </Button>
              ) : (
                <Button variant="hero" size="lg" asChild>
                  <LocalizedLink to="/kontakt">{ctaLabel}</LocalizedLink>
                </Button>
              )}
            </div>

            {/* Mobile: Theme Toggle + Hamburger */}
            <div className="lg:hidden flex items-center gap-1">
              <LanguageSwitcher />
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                aria-label={t('Theme wechseln', 'Toggle theme')}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors duration-200"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                className="p-2 -mr-2"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={t('Menü öffnen', 'Toggle menu')}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden fixed inset-x-0 top-20 z-40 bg-background/98 backdrop-blur-xl border-b border-border/30"
          >
            <nav className="container mx-auto px-4 sm:px-6 py-6 flex flex-col gap-2">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <LocalizedLink
                    to={item.href}
                    className={cn(
                      "block py-3 px-4 rounded-md text-lg font-medium transition-colors",
                      currentBasePath === item.href
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                    )}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </LocalizedLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="pt-4 border-t border-border/30 mt-2"
              >
                {currentBasePath === '/consulting' ? (
                  <Button variant="hero" size="lg" className="w-full" asChild>
                    <a href="#consulting-form" onClick={() => setIsMobileMenuOpen(false)}>
                      {ctaLabel}
                    </a>
                  </Button>
                ) : (
                  <Button variant="hero" size="lg" className="w-full" asChild>
                    <LocalizedLink to="/kontakt" onClick={() => setIsMobileMenuOpen(false)}>
                      {ctaLabel}
                    </LocalizedLink>
                  </Button>
                )}
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
